/**
 * Minimal AWS Signature Version 4 signer for browser fetch, using the built-in
 * Web Crypto API (crypto.subtle) — no third-party dependency.
 *
 * Scoped to what the AI chat stream needs: signing a single POST to a Lambda
 * Function URL (AuthType AWS_IAM) with temporary credentials minted by the API.
 * Returns the headers to attach (Authorization + x-amz-*); the caller sends
 * them with the request. Host and the signed x-amz-* values match what the
 * browser sends, so the signature validates at the origin. Extra headers the
 * caller adds (e.g. x-chat-stream-token) are left unsigned, which SigV4 allows.
 */

export type AwsCredentials = {
  accessKeyId: string
  secretAccessKey: string
  sessionToken: string
}

type SignInput = {
  url: string
  method: string
  body: string
  region: string
  service: string
  credentials: AwsCredentials
}

const encoder = new TextEncoder()

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

async function sha256Hex(data: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(data))

  return toHex(digest)
}

async function hmac(
  key: ArrayBuffer | Uint8Array<ArrayBuffer>,
  data: string
): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  return crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(data))
}

// Builds the SigV4 signing key by chaining HMACs over the credential scope.
async function signingKey(
  secretAccessKey: string,
  dateStamp: string,
  region: string,
  service: string
): Promise<ArrayBuffer> {
  const kDate = await hmac(encoder.encode(`AWS4${secretAccessKey}`), dateStamp)
  const kRegion = await hmac(kDate, region)
  const kService = await hmac(kRegion, service)

  return hmac(kService, 'aws4_request')
}

export async function signAwsRequest({
  url,
  method,
  body,
  region,
  service,
  credentials
}: SignInput): Promise<Record<string, string>> {
  const { host, pathname } = new URL(url)

  const now = new Date()
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '')
  const dateStamp = amzDate.slice(0, 8)
  const payloadHash = await sha256Hex(body)

  const signedHeaders =
    'content-type;host;x-amz-content-sha256;x-amz-date;x-amz-security-token'
  const canonicalHeaders =
    'content-type:application/json\n' +
    `host:${host}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n` +
    `x-amz-security-token:${credentials.sessionToken}\n`

  const canonicalRequest = [
    method,
    pathname || '/',
    '',
    canonicalHeaders,
    signedHeaders,
    payloadHash
  ].join('\n')

  const scope = `${dateStamp}/${region}/${service}/aws4_request`
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    scope,
    await sha256Hex(canonicalRequest)
  ].join('\n')

  const key = await signingKey(
    credentials.secretAccessKey,
    dateStamp,
    region,
    service
  )
  const signature = toHex(await hmac(key, stringToSign))

  const authorization =
    `AWS4-HMAC-SHA256 Credential=${credentials.accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`

  return {
    Authorization: authorization,
    'X-Amz-Date': amzDate,
    'X-Amz-Content-Sha256': payloadHash,
    'X-Amz-Security-Token': credentials.sessionToken
  }
}
